/**
 * Landing — v3 homepage rebuild.
 *
 * Definitive rebuild per the user's v3 mockup + text brief. Replaces the
 * prior heavily-detailed Landing entirely. Scope is intentionally lean:
 *
 *   1. <SharedNav>             (logo + center links + Log in / 2 CTAs)
 *   2. HERO                    (PooPartner pitch — partner-recruitment block)
 *   3. TRUST STRIPE            (full-bleed purple band, 4 trust items)
 *   4. FOR DOG OWNERS          (image LEFT, text RIGHT, single CTA)
 *   5. <SharedFooter>          (3-col Product / Legal block)
 *
 * Everything else from the legacy Landing (track dividers, why-partner
 * grid, how-it-works cards, pricing snippet, customer band, trust grid,
 * legacy footer) is dropped.
 *
 * Style: all visual decisions come from the v3 `--pp-*` design tokens
 * defined in `index.css`. The canonical utility classes (`.pp-wrap`,
 * `.pp-h1`, `.pp-h2`, `.pp-eyebrow`, `.pp-kick`, `.pp-sub`,
 * `.pp-customer-p`, `.pp-proofline`, `.pp-stars`, `.pp-sm`, `.pp-btn`,
 * `.pp-btn-primary`, `.pp-btn-ghost`) carry the typography + button
 * styling so this component stays declarative.
 *
 * Brand-cap rule: "Pluck / Pluckers / Plucking / PooPartner /
 * PooPluckers" always capitalized. Honest-claims rule: anywhere the
 * promise isn't live yet (reviews, neighborhood coverage), the copy
 * explicitly says "coming soon" or "launching with our first
 * neighborhoods".
 *
 * Light + dark both render correctly via `prefers-color-scheme`. WCAG
 * AA verified — see deliverable report.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  Star,
  CalendarDays,
  ArrowRight,
  PawPrint,
} from "lucide-react";
import SharedNav from "@/components/SharedNav";
import ContestPromoTopBar from "@/components/ContestPromoTopBar";
import SharedFooter from "@/components/SharedFooter";
import BrandDivider from "@/components/BrandDivider";
import FeaturedPluckStrip from "@/components/FeaturedPluckStrip";
import WaitlistForm from "@/components/WaitlistForm";
import ContestPromoSlotOverlay from "@/components/ContestPromoSlotOverlay";

/* ---------------------------------------------------------------------------
   Trust stripe data
   --------------------------------------------------------------------------- */
const TRUST_ITEMS = [
  {
    icon: ShieldCheck,
    title: "Local business pros",
    sub: "Business intake with Stripe identity at launch",
  },
  {
    icon: Lock,
    title: "Secure payments",
    sub: "Powered by Stripe",
  },
  {
    icon: Star,
    title: "Rated by neighbors",
    sub: "Coming soon",
  },
  {
    icon: CalendarDays,
    title: "Same-week scheduling",
    sub: "In select areas",
  },
];

/* ---------------------------------------------------------------------------
   Page
   --------------------------------------------------------------------------- */
export default function Landing() {
  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--pp-v3-bg)" }}
      data-testid="landing-v3"
    >
      {/* Scoped hero-CTA size bump. We can't touch `.pp-btn` in index.css
          (brand-guide gate paused token files), and inline `style` can't
          carry media queries — so a tiny scoped block lives here.
          Mobile: 56px tall, 36px padding, 16px text.
          Desktop (≥ 640px): 64px tall, 40px padding, 17px text. */}
      <style>{`
        .pp-btn-hero { font-size: 16px; padding: 0 36px; height: 56px; border-radius: 12px; }
        @media (min-width: 640px) {
          .pp-btn-hero { font-size: 17px; padding: 0 40px; height: 64px; }
        }
      `}</style>

      {/* =========================== NAV =========================== */}
      {/* Item 3 extension (2026-07-24): mount ContestPromoTopBar ABOVE
          the SharedNav so authed-out landing visitors see the promo +
          Enter CTA. Landing.jsx bypasses PublicPageShell (uses SharedNav
          directly), which previously prevented the top bar from
          appearing on `/`. Mounting the component here — instead of
          rewiring Landing to use PublicPageShell — keeps the change
          scoped and non-invasive: `ContestPromoTopBar` self-suppresses
          when no contest is active or when dismissed. */}
      <ContestPromoTopBar />
      {/* Landing is behind `<AuthedRedirect>` — authed users never reach
          this render path. Legacy F1.a auth-aware rightSlot derivation
          was retired in Phase 3 §2. */}
      <SharedNav />

      {/* Phase L.2 P1 — audit L-1. Wrap the page body in a proper
          `<main>` landmark so screen-reader users using landmark
          navigation can jump to main content directly (SC 1.3.1 /
          SC 2.4.1). `id="main-content"` primes for a future skip-link
          (queued P3). */}
      <main id="main-content" data-testid="landing-main">
      {/* =========================== HERO =========================== */}
      <section data-testid="hero-section">
        <div className="pp-wrap grid items-center gap-10 md:grid-cols-2 md:gap-16 py-12 md:py-20">
          {/* Hero copy block */}
          <div>
            <span
              className="pp-eyebrow"
              data-testid="hero-eyebrow"
            >
              <span className="dot" />
              For local service businesses
            </span>

            <h1
              className="pp-h1 mt-6"
              data-testid="hero-headline"
            >
              Add PooPluckers to your business<span
                className="text-pp-orange"
                data-testid="hero-headline-period"
              >.</span>
            </h1>

            <p
              className="pp-sub mt-5"
              data-testid="hero-subhead"
            >
              {/* Pre-launch anticipation copy — Part C (2026-07-21 dispatch).
                  Was: partner-first B2B pitch about existing crew turning
                  into a revenue channel. Kept the partner half; added the
                  "we're launching neighborhood by neighborhood" honest
                  framing so the customer CTA (Join the waitlist, right)
                  makes sense. No launch dates promised. */}
              We're launching PooPluckers neighborhood by neighborhood.
              Partners: turn your crew into a new revenue channel with
              Fast Stripe payouts. Customers: join the waitlist and be
              first in line when booking opens in your area.
            </p>

            {/* CTA row — bumped size for hero (canonical primary conversion
                surface now that the nav no longer carries these). Sizing is
                set inline so we win the cascade against the global `.pp-btn`
                without forking the utility class. */}
            <div
              className="mt-8 flex flex-wrap gap-3 sm:gap-4"
              data-testid="hero-cta-row"
            >
              <Link
                to="/signup?role=provider"
                className="pp-btn pp-btn-primary pp-btn-hero"
                data-testid="hero-partner-cta"
              >
                Become a PooPartner
                <ArrowRight className="arrow h-[18px] w-[18px]" aria-hidden="true" />
              </Link>
              {/* Part C (2026-07-21) — customer CTA renamed
                  "Book a Cleanup" → "Join the waitlist". Anchor-
                  scrolls to the WaitlistForm section at the bottom
                  of the page (#waitlist). Booking flow is NOT live
                  yet; a "Book" CTA at the hero would be dishonest. */}
              <a
                href="#waitlist"
                className="pp-btn pp-btn-ghost pp-btn-hero text-pp-slate"
                data-testid="hero-customer-cta"
              >
                Join the waitlist
              </a>
            </div>

            {/* Proofline — Prod Hotfix Batch 2 P1-4 B2B reframe. Chips
                replaced from gig-voice ("Set your prices" / "Choose
                your area") to B2B-voice matching the "Add PooPluckers
                to your business" pitch. "Fast Stripe payouts" retained
                from the prior fix — the other two chips speak to
                what a service business actually cares about (customer
                pipeline + operational fit). Each chip is wrapped in
                an atomic `inline-flex` span with `whitespace-nowrap`
                so its leading paw + label always stay together on the
                same line. Drift-guard: `Landing.partner-band.test.jsx`. */}
            <div
              className="pp-proofline mt-7"
              data-testid="hero-proofline"
            >
              <span
                className="inline-flex items-center gap-4 whitespace-nowrap"
                data-testid="hero-proofline-chip-1"
              >
                <PawPrint
                  className="h-[14px] w-[14px]"
                  style={{ color: "var(--pp-orange)" }}
                  aria-hidden="true"
                />
                Fast Stripe payouts
              </span>
              <span
                className="inline-flex items-center gap-4 whitespace-nowrap"
                data-testid="hero-proofline-chip-2"
              >
                <PawPrint
                  className="h-[14px] w-[14px]"
                  style={{ color: "var(--pp-orange)" }}
                  aria-hidden="true"
                />
                Ready-to-book customers
              </span>
              <span
                className="inline-flex items-center gap-4 whitespace-nowrap"
                data-testid="hero-proofline-chip-3"
              >
                <PawPrint
                  className="h-[14px] w-[14px]"
                  style={{ color: "var(--pp-orange)" }}
                  aria-hidden="true"
                />
                Route-friendly zips
              </span>
            </div>
          </div>

          {/* Hero visual */}
          <div
            className="flex justify-center md:justify-end"
            data-testid="hero-image-wrap"
          >
            <div className="relative w-full max-w-[560px]">
              <picture>
                <source srcSet="/landing-hero.webp" type="image/webp" />
                <img
                  src="/landing-hero.jpg"
                  alt="A happy dog in a clean, sunny backyard"
                  className="block w-full aspect-[4/5] md:aspect-[5/6] object-cover rounded-[28px]"
                  style={{ boxShadow: "var(--pp-shadow)" }}
                  data-testid="hero-image"
                  loading="eager"
                  fetchPriority="high"
                />
              </picture>
              {/* Contest Promo overlay (2026-07-21 dispatch, Part 2 slot 1
                  of 2). Renders ONLY when a contest round is active AND
                  no winner photo has been assigned to this slot yet.
                  Pre-launch the winner-slot system isn't live for the
                  landing hero, so `hasWinnerPhoto` is a hard `false`
                  until a future dispatch introduces the slot registry. */}
              <ContestPromoSlotOverlay
                hasWinnerPhoto={false}
                testIdSuffix="landing-hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ====================== TRUST STRIPE ====================== */}
      {/* Full-bleed purple band. Inline `background` so the full width is
          painted (the `.pp-wrap` only governs the inner max-width). */}
      <section
        style={{ background: "var(--pp-purple)" }}
        data-testid="trust-stripe-section"
      >
        <div className="pp-wrap py-10 md:py-12">
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 list-none m-0 p-0"
            data-testid="trust-stripe"
          >
            {TRUST_ITEMS.map(({ icon: Icon, title, sub }, idx) => (
              <li
                key={title}
                className="flex items-start gap-4"
                data-testid={`trust-stripe-item-${idx + 1}`}
              >
                <div
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    background: "rgba(255,255,255,0.14)",
                    borderRadius: 10,
                  }}
                  aria-hidden="true"
                >
                  <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                </div>
                <div className="leading-tight">
                  <p
                    className="text-white font-bold text-[15px]"
                    data-testid={`trust-stripe-title-${idx + 1}`}
                  >
                    {title}
                  </p>
                  <p
                    className="text-white/80 text-[13.5px] mt-1 font-medium"
                    data-testid={`trust-stripe-sub-${idx + 1}`}
                  >
                    {sub}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ====================== FOR DOG OWNERS ====================== */}
      <BrandDivider testId="landing-divider-before-dog-owners" />
      <section
        style={{ background: "var(--pp-v3-bg)" }}
        data-testid="dog-owners-section"
      >
        <div className="pp-wrap grid items-center gap-10 md:grid-cols-2 md:gap-16 py-16 md:py-24">
          {/* Image LEFT */}
          <div
            className="order-2 md:order-1 flex justify-center md:justify-start"
            data-testid="dog-owners-image-wrap"
          >
            <picture>
              <source srcSet="/landing-family.webp" type="image/webp" />
              <img
                src="/landing-family.jpg"
                alt="Family with their golden retriever in a sunny backyard."
                className="block w-full max-w-[560px] aspect-[5/6] object-cover rounded-[28px]"
                style={{ boxShadow: "var(--pp-shadow)" }}
                data-testid="dog-owners-image"
                loading="lazy"
              />
            </picture>
          </div>

          {/* Text RIGHT */}
          <div className="order-1 md:order-2">
            <span className="pp-kick" data-testid="dog-owners-kick">
              For dog owners
            </span>
            <h2
              className="pp-h2 mt-3"
              data-testid="dog-owners-headline"
            >
              Book a trusted local{" "}
              <span
                className="text-pp-orange"
                data-testid="dog-owners-headline-accent"
              >
                Pluck
              </span>{" "}
              in minutes.
            </h2>
            <p
              className="pp-customer-p mt-5"
              data-testid="dog-owners-body"
            >
              Tell us about your yard and your pack, and we&apos;ll match
              you with a local PooPartner in your area. Transparent pricing
              and a yard you can actually enjoy.
            </p>

            <div
              className="mt-6 flex items-center gap-3 flex-wrap"
              data-testid="dog-owners-stars-row"
            >
              <span
                className="pp-stars"
                aria-hidden="true"
                data-testid="dog-owners-stars"
              >
                ★★★★★
              </span>
              <span className="pp-sm" data-testid="dog-owners-stars-sub">
                Reviews launching with our first neighborhoods
              </span>
            </div>

            <div className="mt-8">
              <Link
                to="/waitlist"
                className="pp-btn pp-btn-primary"
                data-testid="dog-owners-cta"
              >
                Join the waitlist
                <ArrowRight className="arrow h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== FEATURED PLUCKS (Cutest Pluck Phase C) ======================
          Renders NOTHING (returns null) when no winners have been ranked
          — LOCKED per Phase C dispatch. Once the admin assigns ranks 1-3,
          this strip populates. Positioned BELOW landing_family / dog-owners
          section and ABOVE the shared footer per the additive-only rule. */}
      <FeaturedPluckStrip />

      {/* ================= WAITLIST (Part D — Pre-Launch Demand Capture) =================
          Added 2026-07-21. Anchor `#waitlist` — hero customer CTA
          `Join the waitlist` scrolls here. Section headline uses
          Guard 11 phrase-accent orange. The form component owns the
          Turnstile + confirmation state; this section is layout only. */}
      <section
        className="pp-wrap py-16 md:py-24"
        data-testid="landing-waitlist-section"
      >
        <div className="mx-auto max-w-3xl">
          <h2
            className="pp-h2 text-center"
            data-testid="landing-waitlist-headline"
          >
            <span className="text-pp-orange">Be first in line</span>{" "}
            when booking opens in your area.
          </h2>
          <p
            className="pp-sub mt-4 text-center"
            data-testid="landing-waitlist-subhead"
          >
            We're rolling out neighborhood by neighborhood. Drop your
            info and we'll email you the moment PooPluckers is live in
            your ZIP.
          </p>
          <div className="mt-8">
            <WaitlistForm />
          </div>
        </div>
      </section>
      </main>

      {/* =========================== FOOTER =========================== */}
      <SharedFooter />
    </div>
  );
}
